import apiClient from '@/api/client';

export interface AdminUser {
  id: number;
  email: string;
  display_name: string;
  is_admin: boolean;
  is_active: boolean;
}

export interface AccessRow {
  mailbox_id: number;
  address: string;
  display_name: string;
  can_read: boolean;
  can_reply: boolean;
  can_filter_admin: boolean;
  can_send_as_customer: boolean;
}

export interface UserCreate {
  email: string;
  display_name: string;
  password: string;
  is_admin: boolean;
}

export interface UserUpdate {
  display_name?: string;
  password?: string;
  is_admin?: boolean;
  is_active?: boolean;
}

export async function listUsers(): Promise<AdminUser[]> {
  const { data } = await apiClient.get<AdminUser[]>('/admin/users');
  return data;
}

export async function createUser(payload: UserCreate): Promise<AdminUser> {
  const { data } = await apiClient.post<AdminUser>('/admin/users', payload);
  return data;
}

export async function updateUser(id: number, payload: UserUpdate): Promise<AdminUser> {
  const { data } = await apiClient.patch<AdminUser>(`/admin/users/${id}`, payload);
  return data;
}

export async function deactivateUser(id: number): Promise<void> {
  await apiClient.delete(`/admin/users/${id}`);
}

export async function getUserAccess(id: number): Promise<AccessRow[]> {
  const { data } = await apiClient.get<AccessRow[]>(`/admin/users/${id}/access`);
  return data;
}

export async function setUserAccess(id: number, items: AccessRow[]): Promise<AccessRow[]> {
  const { data } = await apiClient.put<AccessRow[]>(`/admin/users/${id}/access`, { items });
  return data;
}

// ---- shared mailbox (IMAP account) CRUD ----

export interface AdminMailbox {
  id: number;
  address: string;
  display_name: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
  is_active: boolean;
}

export interface MailboxCreate {
  address: string;
  display_name: string;
  imap_password: string;
  imap_host?: string;
  imap_port?: number;
  smtp_host?: string;
  smtp_port?: number;
}

export interface MailboxUpdate {
  display_name?: string;
  imap_password?: string;
  imap_host?: string;
  imap_port?: number;
  smtp_host?: string;
  smtp_port?: number;
  is_active?: boolean;
}

export async function listMailboxesAdmin(): Promise<AdminMailbox[]> {
  const { data } = await apiClient.get<AdminMailbox[]>('/admin/mailboxes');
  return data;
}

export async function createMailbox(p: MailboxCreate): Promise<AdminMailbox> {
  const { data } = await apiClient.post<AdminMailbox>('/admin/mailboxes', p);
  return data;
}

export async function updateMailbox(id: number, p: MailboxUpdate): Promise<AdminMailbox> {
  const { data } = await apiClient.patch<AdminMailbox>(`/admin/mailboxes/${id}`, p);
  return data;
}

export async function deactivateMailbox(id: number): Promise<void> {
  await apiClient.delete(`/admin/mailboxes/${id}`);
}

export async function testMailbox(id: number): Promise<{ ok: boolean; detail?: string }> {
  const { data } = await apiClient.post<{ ok: boolean; detail?: string }>(`/admin/mailboxes/${id}/test`);
  return data;
}

// ---- audit log ----

export interface AuditEntry {
  id: number;
  created_at: string;
  user_email: string | null;
  event_type: string;
  target: string | null;
  detail: string | null;
  ip: string | null;
}

export interface AuditPage {
  total: number;
  items: AuditEntry[];
  types: string[];
}

export interface LogFilters {
  event_type?: string;
  user_email?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export async function listLogs(f: LogFilters): Promise<AuditPage> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(f)) if (v) params[k] = v as string | number;
  const { data } = await apiClient.get<AuditPage>('/admin/logs', { params });
  return data;
}

// ---- fail2ban (own jail only) ----

export interface Fail2banStatus {
  jail: string;
  currently_banned: number;
  total_banned: number;
  currently_failed: number;
  total_failed: number;
  banned_ips: string[];
  available: boolean;
  error: string | null;
}

export async function getFail2banStatus(): Promise<Fail2banStatus> {
  const { data } = await apiClient.get<Fail2banStatus>('/admin/fail2ban');
  return data;
}

export async function banIp(ip: string): Promise<Fail2banStatus> {
  const { data } = await apiClient.post<Fail2banStatus>('/admin/fail2ban/ban', { ip });
  return data;
}

export async function unbanIp(ip: string): Promise<Fail2banStatus> {
  const { data } = await apiClient.post<Fail2banStatus>('/admin/fail2ban/unban', { ip });
  return data;
}
